import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CURRENT_CLIENT_TEMPLATE_VAR, DeepPartial, EntityStatus } from '@core';
import { type ImageDocument, type SpaceplanTabType } from '@space-plan/models';
import { map, Observable } from 'rxjs';

import { EntitySummary, LocationBaseModel, LocationData, Space } from '../models';

@Injectable({ providedIn: 'root' })
export class LocationService {
  private http = inject(HttpClient);

  getLocationsList(includeArchived = true): Observable<LocationData[]> {
    const locationStatus = [EntityStatus.Active, EntityStatus.Paused];

    if (includeArchived) {
      locationStatus.push(EntityStatus.Archived);
    }
    const queryString = locationStatus.map(status => `status=${status}`).join('&');

    return this.http
      .get<{ data: LocationData[]; }>(`locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations?${queryString}`)
      .pipe(map(({ data }) => data));
  }

  getLocationsSummaries(clientId: string, params: Record<string, any>) {
    return this.http
      .get<{ data: { id: string; summary: EntitySummary; }[]; }>(`locations/v1/clients/${clientId}/locations-summaries`, {
        params,
      })
      .pipe(map(({ data }) => data));
  }

  addLocation(clientId: string, data: LocationBaseModel) {
    return this.http.post<{ data: LocationData; }>(`locations/v1/clients/${clientId}/locations`, data).pipe(map(({ data }) => data));
  }

  updateLocation(
    clientId: string,
    locationId: string,
    { id, name, clientFriendlyId, ...data }: DeepPartial<LocationData>,
  ) {
    return this.http
      .patch<{ data: LocationData; }>(`locations/v1/clients/${clientId}/locations/${locationId}`, data)
      .pipe(map(({ data }) => data));
  }

  getLocationInfoById(locationId: string): Observable<LocationData> {
    return this.http
      .get<{
        data: LocationData;
      }>(`locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations/${locationId}`, {
        params: {
          deviceStatus: ['active', 'paused'],
        },
      })
      .pipe(map(({ data }) => data));
  }

  deleteLocation(locationId: string) {
    return this.http.delete(
      `locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations/${locationId}`,
    );
  }

  getSpacesList(locationId: string, params: Record<string, string> = {}): Observable<Space[]> {
    return this.http
      .get<{
        data: Space[];
      }>(`locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations/${locationId}/spaces`, { params })
      .pipe(map(({ data }) => data));
  }

  getSpaceInfoById(locationId: string, spaceId: string): Observable<Space> {
    return this.http
      .get<{
        data: Space;
      }>(`locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations/${locationId}/spaces/${spaceId}`, {
        params: {
          deviceStatus: ['active', 'paused'],
        },
      })
      .pipe(map(({ data }) => data));
  }

  addNewSpace<T = Space>(clientId: string, locationId: string, data: Partial<Space>) {
    return this.http
      .post<{ data: T; }>(`locations/v1/clients/${clientId}/locations/${locationId}/spaces`, data)
      .pipe(map(({ data }) => data));
  }

  updateSpace<T = Space>(clientId: string, locationId: string, spaceId: string, data: DeepPartial<Space>) {
    return this.http
      .patch<{ data: T; }>(`locations/v1/clients/${clientId}/locations/${locationId}/spaces/${spaceId}`, data)
      .pipe(map(({ data }) => data));
  }

  bulkSpaceReorder<T = Space[]>(clientId: string, locationId: string, data: Partial<Space>[]) {
    return this.http
      .patch<{ data: T; }>(`locations/v1/clients/${clientId}/locations/${locationId}/spaces/reorder`, { spaces: data })
      .pipe(map(({ data }) => data));
  }

  uploadDocumentToSpace({ clientId, locationId, spaceId, body, tab }: {
    body: File;
    clientId: string;
    locationId: string;
    spaceId: string;
    tab: SpaceplanTabType,
  }) {
    const params: { [key: string]: string | boolean; } = {};

    params['tab'] = tab;

    return this.http
      .post<{ data: ImageDocument; }>(`locations/v1/clients/${clientId}/locations/${locationId}/spaces/${spaceId}/documents`, body, {
        headers: new HttpHeaders({
          'Content-Type': 'image/svg+xml',
          FileName: body.name,
        }),
        params,
      })
      .pipe(map(({ data }) => data));
  }

  deleteDocumentFromSpace({ clientId, locationId, spaceId, filename }: { [_key: string]: string; }) {
    return this.http.delete(
      `locations/v1/clients/${clientId}/locations/${locationId}/spaces/${spaceId}/documents/${filename}?removeReference=true`,
    );
  }

  getAllSpaceDocuments({ clientId, locationId, spaceId }: { [_key: string]: string; }) {
    return this.http
      .get<{ data: ImageDocument[]; }>(`locations/v1/clients/${clientId}/locations/${locationId}/spaces/${spaceId}/documents`)
      .pipe(map(({ data }) => data));
  }

  getSpaceDocument({ clientId, locationId, spaceId, filename }: { [_key: string]: string; }) {
    return this.http.get(`locations/v1/clients/${clientId}/locations/${locationId}/spaces/${spaceId}/documents/${filename}`, {
      responseType: 'blob',
    });
  }

  getSpacesSummaries(clientId: string, locationId: string, params: Record<string, any> = {}) {
    return this.http
      .get<{ data: { id: string; summary: EntitySummary; }[]; }>(
        `locations/v1/clients/${clientId}/locations/${locationId}/spaces-summaries`,
        {
          params,
        },
      )
      .pipe(map(({ data }) => data));
  }

  deleteSpace(locationId: string, spaceId: string) {
    return this.http.delete(
      `locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations/${locationId}/spaces/${spaceId}`,
    );
  }

  cloneRoom(
    locationId: string,
    roomId: string,
    body: {
      cloneNewFields: {
        cloneBlockDiagrams: boolean;
        cloneDevices: boolean;
        floorId: string;
        locationId: string;
        name: string;
      }[];
      numberOfClones: number;
    }) {
    return this.http.post(
      `locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations/${locationId}/spaces/${roomId}/clone-room`,
      body,
    );
  }
}
